import { useEffect, useCallback, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { registerUnsavedChanges, unregisterUnsavedChanges } from '../utils/unsavedChangesRegistry';

interface UseUnsavedChangesWarningOptions {
  /** Whether the form has unsaved changes */
  isDirty: boolean;
  /** Optional callback when user confirms they want to leave */
  onConfirmLeave?: () => void;
}

export interface UseUnsavedChangesWarningReturn {
  /** Whether the blocker dialog should be shown */
  showBlockerDialog: boolean;
  /** Call to confirm navigation (user chose to leave) */
  confirmNavigation: () => void;
  /** Call to cancel navigation (user chose to stay) */
  cancelNavigation: () => void;
}

/**
 * Hook to warn users about unsaved changes when navigating away. Handles:
 *  - browser navigation (refresh / close tab / external links) via `beforeunload`
 *  - browser back / forward via `popstate`
 *  - in-app react-router `<Link>` clicks via a capture-phase document click
 *    interceptor (see below)
 *  - programmatic `navigate()` from a cross-cutting navigator — the sidebar, the
 *    module switcher — via the unsaved-changes registry, which those navigators
 *    consult through `useGuardedNavigate()` before moving.
 *
 * Note: the app uses BrowserRouter, which has no `useBlocker` (that needs a data
 * router). Instead, in-app link clicks are caught in the capture phase before
 * react-router's delegated onClick runs. Known limitations of the DOM-click
 * approach (all acceptable — the common data-loss paths, a nav-link click and a
 * sidebar entry, are covered):
 *  - A programmatic `navigate()` bypasses anchors entirely, so it is caught only
 *    where the caller opts in via `useGuardedNavigate()`. A form's own Cancel
 *    button navigates programmatically too and owes its own confirm dialog.
 *  - A `<Link state={...}>` / `<Link replace>` is resumed via a plain push
 *    without its `state`/`replace` semantics (they live on the React props, not
 *    on the rendered `<a>`), so those are not preserved across the prompt.
 *  - The listener is document-global (like the beforeunload/popstate handlers
 *    below); two concurrently-mounted dirty forms would each pop their own
 *    dialog on an anchor click.
 *  - The back-detection sentinel is pushed when the form becomes dirty and is
 *    not popped when it is saved (nothing may pop a history entry a user did
 *    not act on), so the first Back press after saving is absorbed by it.
 * Removing these requires migrating to createBrowserRouter + useBlocker.
 *
 * @example
 * const { showBlockerDialog, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning({
 *   isDirty: formState.isDirty,
 * });
 *
 * // In your JSX:
 * <ConfirmDialog
 *   open={showBlockerDialog}
 *   onConfirm={confirmNavigation}
 *   onCancel={cancelNavigation}
 *   title={t('common:app.unsavedChanges')}
 *   message={t('common:app.unsavedChangesMessage')}
 *   confirmLabel={t('common:app.leave')}
 *   cancelLabel={t('common:app.stay')}
 * />
 */
export function useUnsavedChangesWarning({
  isDirty,
  onConfirmLeave,
}: UseUnsavedChangesWarningOptions): UseUnsavedChangesWarningReturn {
  const [showBlockerDialog, setShowBlockerDialog] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);
  const pushedStatesCountRef = useRef(0);
  // Set while confirmNavigation() rewinds the history stack. The form is still
  // dirty at that point (the user chose to discard, not to save), so without this
  // the popstate our own history.go() delivers would be treated as a fresh Back
  // press and blocked again.
  const isRewindingRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isDirtyRef = useRef(isDirty);

  // Keep isDirtyRef in sync
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Feed the global unsaved-changes registry so cross-cutting side effects
  // (e.g. the silent version auto-refresh) can avoid reloading a dirty page, and
  // so a PROGRAMMATIC navigator (a chrome <button> calling navigate(), which the
  // anchor interceptor below cannot see) can hand us its pending destination and
  // let this form's own dialog decide. `confirmNavigation` then resumes it, so the
  // guarded path and the anchor path converge on one prompt and one navigate().
  useEffect(() => {
    if (!isDirty) return;
    const token = Symbol('unsaved-changes');
    registerUnsavedChanges(token, (destination: string) => {
      pendingNavigationRef.current = destination;
      setShowBlockerDialog(true);
    });
    return () => unregisterUnsavedChanges(token);
  }, [isDirty]);

  // Block browser navigation (refresh, close tab, external links)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Intercept browser back/forward navigation.
  //
  // Gated on `isDirty`: the sentinel entry below is what lets us detect a Back
  // press, but pushing it while the form is CLEAN swallows the user's first Back
  // press on every page that mounts this hook — the page just doesn't navigate.
  // There is nothing to protect until the form is dirty, so arm it then.
  useEffect(() => {
    if (!isDirty) return;

    const handlePopState = () => {
      if (isRewindingRef.current) {
        // Our own history.go() landing — let it through.
        isRewindingRef.current = false;
        return;
      }
      if (isDirtyRef.current) {
        // Push the current location back to prevent navigation
        window.history.pushState(null, '', location.pathname + location.search);
        pushedStatesCountRef.current++;
        // Store the attempted destination
        pendingNavigationRef.current = '__back__';
        setShowBlockerDialog(true);
      }
    };

    // Push the sentinel state so we can detect the back button. Count up rather
    // than assign: a pushed entry is never popped by this effect's cleanup, so
    // under StrictMode's double-invoke (dev) two sentinels exist and the rewind
    // in confirmNavigation must clear both.
    window.history.pushState(null, '', location.pathname + location.search);
    pushedStatesCountRef.current += 1;
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty, location.pathname, location.search]);

  // Intercept in-app <Link>/<a> clicks. BrowserRouter has no useBlocker, so we
  // catch the click in the capture phase on `document` — which fires before
  // react-router's delegated onClick (React 18 attaches its listeners to the
  // #root container, an inner element) — and stop propagation so <Link> never
  // navigates. The chosen destination is deferred to confirmNavigation().
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!isDirtyRef.current || e.defaultPrevented) return;
      // Let the browser handle new-tab/window and non-primary-button clicks.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as Element | null)?.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || anchor.hasAttribute('download')) return;
      const target = anchor.getAttribute('target');
      if (target && target !== '_self') return;

      // Only intercept same-origin in-app routes; external links unload the
      // document and are covered by the beforeunload handler above.
      const destinationUrl = new URL(anchor.href, window.location.href);
      if (destinationUrl.origin !== window.location.origin) return;

      // Compare by path+search only: a hash-only change on the same page does
      // not unmount the form, so it shouldn't trigger the prompt.
      const currentPath = window.location.pathname + window.location.search;
      const nextPath = destinationUrl.pathname + destinationUrl.search;
      if (nextPath === currentPath) return;

      const next = nextPath + destinationUrl.hash;

      e.preventDefault();
      e.stopPropagation();
      pendingNavigationRef.current = next;
      setShowBlockerDialog(true);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  const confirmNavigation = useCallback(() => {
    onConfirmLeave?.();
    setShowBlockerDialog(false);
    const destination = pendingNavigationRef.current;
    // Rewind by exactly the sentinels we pushed: the popstate handler already
    // re-pushed the current location, so the entry `stepsBack` behind us IS the
    // user's Back destination. Overshooting walks off the end of the SPA's
    // history and unloads the document instead of navigating within it.
    const stepsBack = pushedStatesCountRef.current;
    pendingNavigationRef.current = null;
    pushedStatesCountRef.current = 0;

    if (destination === '__back__') {
      // Go back past all the states we pushed
      isRewindingRef.current = true;
      window.history.go(-stepsBack);
    } else if (destination) {
      navigate(destination);
    }
  }, [navigate, onConfirmLeave]);

  const cancelNavigation = useCallback(() => {
    setShowBlockerDialog(false);
    pendingNavigationRef.current = null;
  }, []);

  return {
    showBlockerDialog,
    confirmNavigation,
    cancelNavigation,
  };
}
